// AudioWaveform — fetches an audio URL, decodes via Web Audio, and draws a
// downsampled waveform onto a canvas. Used on AssetCard for an at-a-glance
// preview of audio assets.
//
// Decoded buffers are cached in a module-level WeakMap so repeated mounts
// don't re-fetch. The cache key is the URL string.
//
// Cost: a single fetch + decodeAudioData per asset, ~50–200ms on a 4 MB WAV.

import React, { useEffect, useRef, useState } from 'react'

const _peakCache = new Map() // url -> Float32Array of normalized peaks

// Reuse one AudioContext for decode-only work; never start it (no playback
// → no autoplay-policy unlock needed).
let _ctx = null
function getCtx() {
  if (_ctx) return _ctx
  try { _ctx = new (window.AudioContext || window.webkitAudioContext)() } catch { _ctx = null }
  return _ctx
}

// Downsample channel data into `bins` peaks (max abs amplitude per bin).
function buildPeaks(channelData, bins) {
  const out = new Float32Array(bins)
  const len = channelData.length
  const step = Math.max(1, Math.floor(len / bins))
  let max = 0
  for (let i = 0; i < bins; i++) {
    let peak = 0
    const start = i * step
    const end = Math.min(len, start + step)
    for (let j = start; j < end; j++) {
      const v = Math.abs(channelData[j])
      if (v > peak) peak = v
    }
    out[i] = peak
    if (peak > max) max = peak
  }
  if (max > 0) for (let i = 0; i < bins; i++) out[i] /= max
  return out
}

export default function AudioWaveform({ url, width = 160, height = 36, color = '#bec2ff' }) {
  const canvasRef = useRef(null)
  const [status, setStatus] = useState('loading')

  useEffect(() => {
    let cancelled = false
    setStatus('loading')

    const bins = Math.max(40, Math.floor(width / 2))
    const draw = peaks => {
      if (cancelled) return
      const c = canvasRef.current
      if (!c) return
      const dpr = window.devicePixelRatio || 1
      c.width = width * dpr; c.height = height * dpr
      c.style.width = `${width}px`; c.style.height = `${height}px`
      const ctx = c.getContext('2d')
      ctx.scale(dpr, dpr)
      ctx.clearRect(0, 0, width, height)
      const mid = height / 2
      const barWidth = width / peaks.length
      ctx.fillStyle = color
      for (let i = 0; i < peaks.length; i++) {
        const h = Math.max(1, peaks[i] * mid)
        ctx.fillRect(i * barWidth, mid - h, Math.max(1, barWidth - 0.5), h * 2)
      }
      setStatus('ready')
    }

    if (_peakCache.has(url)) {
      draw(_peakCache.get(url))
      return () => { cancelled = true }
    }

    const ctx = getCtx()
    if (!ctx) { setStatus('error'); return () => { cancelled = true } }

    fetch(url)
      .then(r => {
        if (!r.ok) throw new Error(`fetch ${r.status}`)
        return r.arrayBuffer()
      })
      .then(buf => ctx.decodeAudioData(buf))
      .then(audio => {
        if (cancelled) return
        const peaks = buildPeaks(audio.getChannelData(0), bins)
        _peakCache.set(url, peaks)
        draw(peaks)
      })
      .catch(e => {
        if (!cancelled) {
          console.warn('[AudioWaveform] decode failed for', url, e)
          setStatus('error')
        }
      })

    return () => { cancelled = true }
  }, [url, width, height, color])

  return (
    <div style={{ width, height, position: 'relative' }}>
      <canvas ref={canvasRef} style={{ display: 'block' }} />
      {status !== 'ready' && (
        <div style={{
          position: 'absolute', inset: 0,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontFamily: 'var(--m-font)', fontSize: 9, color: '#8f8fa2',
        }}>{status === 'loading' ? '∿ decoding…' : 'no preview'}</div>
      )}
    </div>
  )
}
